import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock3,
  FileText,
  Filter,
  Info,
  Layers,
  Search,
  ShieldAlert,
  Trash2,
  XCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const statusTabs = ["All", "Open", "Reviewing", "Resolved", "Dismissed"];
const typeTabs = ["All", "Grievance", "Request"];

const displayType = (type) => type === "Complaint" ? "Grievance" : (type || "Grievance");

const statusStyle = {
  Open: "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] border-[var(--accent-primary)]/20 dark:text-[var(--accent-primary)]",
  Reviewing: "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
  Resolved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  Dismissed: "bg-[var(--bg-secondary)]0/10 text-slate-600 border-slate-500/20 dark:text-slate-400",
};

const activeStyles = {
  All: "border-blue-500 bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] dark:text-[var(--accent-primary)] dark:border-blue-500/40 shadow-sm",
  Open: "border-sky-500 bg-sky-500/10 text-sky-600 dark:text-sky-400 dark:border-sky-500/40 shadow-sm",
  Reviewing: "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400 dark:border-amber-500/40 shadow-sm",
  Resolved: "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 dark:border-emerald-500/40 shadow-sm",
  Dismissed: "border-slate-500 bg-[var(--bg-secondary)]0/10 text-slate-600 dark:text-slate-400 dark:border-slate-500/40 shadow-sm",
};

const statusPillStyles = {
  All: "border-blue-500 bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] dark:text-[var(--accent-primary)]",
  Open: "border-sky-500 bg-sky-500/10 text-sky-600 dark:text-sky-400",
  Reviewing: "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  Resolved: "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  Dismissed: "border-slate-500 bg-[var(--bg-secondary)]0/10 text-slate-600 dark:text-slate-400",
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const employeeLine = (employee) => {
  if (!employee) return "Employee not found";
  return `${employee.name || "Employee"} - ${employee.employeeId || "-"}`;
};

const MetricCard = ({ title, value, icon, type, isActive, onClick }) => {
  const activeStyle = activeStyles[type] || activeStyles.All;
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl border p-5 shadow-sm transition-all duration-300 relative overflow-hidden group ${
        isActive
          ? `${activeStyle} -translate-y-1`
          : "border-[var(--border-color)] bg-[var(--card-bg)] hover:border-[var(--accent-primary)]/20 hover:-translate-y-0.5"
      }`}
    >
      <div className="flex items-center justify-between w-full">
        <div>
          <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)]">{title}</p>
          <p className="mt-2 text-2xl font-black tracking-tight">{value}</p>
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110 ${
          isActive ? "bg-[var(--card-bg)]/20 dark:bg-[var(--card-bg)]/5" : "bg-[var(--bg-primary)] text-[var(--text-secondary)]"
        }`}>
          {icon}
        </div>
      </div>
    </button>
  );
};

const AdminComplaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [indicator, setIndicator] = useState("");

  // Custom Modals State
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteModalComplaint, setNoteModalComplaint] = useState(null);
  const [noteModalTargetStatus, setNoteModalTargetStatus] = useState("");
  const [noteText, setNoteText] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalComplaint, setDeleteModalComplaint] = useState(null);

  const fetchComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/admin/complaints", {
        params: {
          status,
          type,
          query: search.trim() || undefined,
          limit: 160,
        },
        cache: false,
      });
      setComplaints(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load complaints:", error);
    } finally {
      setLoading(false);
    }
  }, [search, status, type]);

  useEffect(() => {
        document.title = "Grievance | Savrion Technologies";
    const timeoutId = window.setTimeout(fetchComplaints, search.trim() ? 250 : 0);
    return () => window.clearTimeout(timeoutId);
  }, [fetchComplaints, search]);

  const counts = useMemo(() => {
    return complaints.reduce((acc, complaint) => {
      acc[complaint.status] = (acc[complaint.status] || 0) + 1;
      acc.All += 1;
      return acc;
    }, { All: 0, Open: 0, Reviewing: 0, Resolved: 0, Dismissed: 0 });
  }, [complaints]);

  const handleUpdateStatusClick = (complaint, nextStatus) => {
    if (["Resolved", "Dismissed"].includes(nextStatus)) {
      setNoteModalComplaint(complaint);
      setNoteModalTargetStatus(nextStatus);
      setNoteText(complaint.adminNote || "");
      setNoteModalOpen(true);
    } else {
      executeStatusUpdate(complaint, nextStatus, complaint.adminNote || "");
    }
  };

  const executeStatusUpdate = async (complaint, nextStatus, noteValue) => {
    setUpdatingId(complaint._id);
    setIndicator(`Marking grievance as ${nextStatus}...`);
    const previous = complaints;
    setComplaints((current) =>
      current.map((item) =>
        item._id === complaint._id
          ? { ...item, status: nextStatus, adminNote: noteValue || item.adminNote }
          : item,
      ),
    );

    try {
      const res = await api.patch(`/api/admin/complaints/${complaint._id}`, {
        status: nextStatus,
        adminNote: noteValue || "",
      }, { skipSaveToast: true });
      setComplaints((current) => current.map((item) => item._id === complaint._id ? res.data : item));
      setIndicator("Grievance updated.");
    } catch (error) {
      setComplaints(previous);
      setIndicator(error.response?.data?.message || "Could not update grievance.");
    } finally {
      setUpdatingId("");
      window.setTimeout(() => setIndicator(""), 2200);
    }
  };

  const handleDeleteClick = (complaint) => {
    setDeleteModalComplaint(complaint);
    setDeleteModalOpen(true);
  };

  const executeDelete = async (complaint) => {
    setUpdatingId(complaint._id);
    setIndicator("Deleting grievance...");
    const previous = complaints;
    setComplaints((current) => current.filter((item) => item._id !== complaint._id));

    try {
      await api.delete(`/api/admin/complaints/${complaint._id}`, { skipSaveToast: true });
      setIndicator("Grievance deleted.");
    } catch (error) {
      setComplaints(previous);
      setIndicator(error.response?.data?.message || "Could not delete grievance.");
    } finally {
      setUpdatingId("");
      window.setTimeout(() => setIndicator(""), 2200);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
      <AdminNavbar />

      <main className="min-w-0 flex-1 px-4 py-6 md:px-8">
        <div className="mx-auto w-full max-w-[1400px] space-y-6">
          
          {/* Header Card with flat border and subtle header background */}
          <section className="rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] border border-[var(--accent-primary)]/20 dark:text-[var(--accent-primary)]">
                  <ShieldAlert size={26} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-[var(--accent-primary)]/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)] dark:text-[var(--accent-primary)]">
                      Security & Admin
                    </span>
                  </div>
                  <h1 className="mt-1 text-2xl font-black tracking-tight md:text-3xl bg-clip-text text-transparent bg-gradient-to-r from-[var(--text-primary)] to-[var(--text-secondary)]">
                    Grievance & Request Inbox
                  </h1>
                  <p className="mt-1.5 text-xs font-semibold text-[var(--text-secondary)]">
                    Review and resolve grievances and requests raised by employees across all teams.
                  </p>
                </div>
              </div>

              {indicator && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="rounded-xl border border-[var(--accent-soft)] bg-[var(--accent-light)] dark:border-[var(--border-color)] dark:bg-[var(--accent-subtle)] px-4 py-2.5 text-xs font-bold text-[var(--heading-secondary)] dark:text-[var(--heading-secondary)] shadow-sm"
                >
                  {indicator}
                </motion.div>
              )}
            </div>
          </section>

          {/* Metrics Dashboard */}
          <section className="grid gap-4 grid-cols-2 lg:grid-cols-5">
            <MetricCard 
              title="All Items" 
              value={counts.All} 
              icon={<Layers size={18} />} 
              type="All"
              isActive={status === "All"}
              onClick={() => setStatus("All")}
            />
            <MetricCard 
              title="Open" 
              value={counts.Open} 
              icon={<Clock3 size={18} />} 
              type="Open"
              isActive={status === "Open"}
              onClick={() => setStatus("Open")}
            />
            <MetricCard 
              title="Reviewing" 
              value={counts.Reviewing} 
              icon={<Activity size={18} />} 
              type="Reviewing"
              isActive={status === "Reviewing"}
              onClick={() => setStatus("Reviewing")}
            />
            <MetricCard 
              title="Resolved" 
              value={counts.Resolved} 
              icon={<CheckCircle2 size={18} />} 
              type="Resolved"
              isActive={status === "Resolved"}
              onClick={() => setStatus("Resolved")}
            />
            <MetricCard 
              title="Dismissed" 
              value={counts.Dismissed} 
              icon={<XCircle size={18} />} 
              type="Dismissed"
              isActive={status === "Dismissed"}
              onClick={() => setStatus("Dismissed")}
            />
          </section>

          {/* Filters and Controls */}
          <section className="rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              
              {/* Status Tabs Pills */}
              <div className="flex flex-wrap gap-2">
                {statusTabs.map((item) => {
                  const activePillStyle = statusPillStyles[item] || statusPillStyles.All;
                  return (
                    <button
                      type="button"
                      key={item}
                      onClick={() => setStatus(item)}
                      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-xs font-black transition-all duration-200 ${
                        status === item
                          ? `${activePillStyle} shadow-sm`
                          : "border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)]/20 hover:text-[var(--text-primary)]"
                      }`}
                    >
                      <span>{item}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        status === item 
                          ? "bg-[var(--card-bg)]/20" 
                          : "bg-[var(--border-color)] text-[var(--text-secondary)]"
                      }`}>
                        {counts[item]}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Segmented Type Controller and Search */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                
                {/* Type Tab Segmented Controls */}
                <div className="inline-flex rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-1">
                  {typeTabs.map((item) => (
                    <button
                      type="button"
                      key={item}
                      onClick={() => setType(item)}
                      className={`rounded-lg px-4 py-1.5 text-xs font-black transition-all duration-150 ${
                        type === item
                          ? "bg-[var(--card-bg)] text-[var(--text-primary)] shadow-sm border border-[var(--border-color)]"
                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>

                {/* Search Bar */}
                <div className="relative min-w-[240px] flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search name, ID or grievance..."
                    className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-3 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                  />
                </div>

              </div>

            </div>
          </section>

          {/* Grievances Feed */}
          <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-[var(--border-color)] pb-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[var(--text-secondary)]">
                <Filter size={14} className="text-[var(--accent-primary)]" />
                <span>{loading ? "Searching records..." : `${complaints.length} Records Found`}</span>
              </div>
            </div>

            {loading ? (
              <div className="rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-12 text-center text-sm font-semibold">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[var(--text-secondary)]">Loading grievances & requests...</p>
              </div>
            ) : !complaints.length ? (
              <div className="rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-12 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]">
                  <ShieldAlert size={24} />
                </div>
                <p className="font-black text-sm text-[var(--text-primary)]">No grievances or requests found.</p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">Try clearing your filters or adjusting your search keyword.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <AnimatePresence initial={false}>
                  {complaints.map((complaint) => {
                    const raisedInitial = complaint.raisedBy?.name?.charAt(0)?.toUpperCase() || "E";
                    const againstInitial = complaint.type === "Request" ? "A" : (complaint.againstEmployee?.name?.charAt(0)?.toUpperCase() || "E");
                    const stStyle = statusStyle[complaint.status] || statusStyle.Open;
                    
                    return (
                      <motion.article 
                        key={complaint._id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.25 }}
                        className="relative overflow-hidden rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 shadow-sm hover:shadow-md hover:border-[var(--accent-primary)]/20 transition-all duration-300"
                      >
                        {/* Ticket Header */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[var(--border-color)] pb-4 mb-4">
                          <div className="space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider border ${stStyle}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                                {complaint.status || "Open"}
                              </span>
                              <span className="rounded-full bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 px-2.5 py-0.5 text-[10px] font-black text-[var(--accent-primary)] dark:text-[var(--accent-primary)] uppercase tracking-wider">
                                {displayType(complaint.type)}
                              </span>
                              <span className="text-[10px] font-semibold text-[var(--text-secondary)] flex items-center gap-1">
                                <Calendar size={12} />
                                {formatDate(complaint.createdAt)}
                              </span>
                            </div>
                            <h2 className="text-base font-black tracking-tight text-[var(--text-primary)]">
                              {complaint.subject || displayType(complaint.type)}
                            </h2>
                          </div>
                        </div>

                        {/* Ticket Participants Section */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-2xl p-4 mb-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] border border-[var(--accent-primary)]/20 dark:text-[var(--accent-primary)] font-black text-xs shadow-sm">
                              {raisedInitial}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[9px] font-black uppercase tracking-wider text-[var(--text-secondary)]">Raised By</p>
                              <p className="truncate text-xs font-black text-[var(--text-primary)]" title={employeeLine(complaint.raisedBy)}>
                                {complaint.raisedBy?.name || "Unknown"}
                              </p>
                              <p className="truncate text-[9px] text-[var(--text-secondary)] mt-0.5">
                                {complaint.raisedBy?.team || "No Team"} • ID: {complaint.raisedBy?.employeeId || "-"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-center text-[var(--text-secondary)] shrink-0 px-2">
                            <ArrowRight size={16} className="rotate-90 sm:rotate-0" />
                          </div>

                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-500/10 text-rose-600 border border-rose-500/20 dark:text-rose-400 font-black text-xs shadow-sm">
                              {againstInitial}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[9px] font-black uppercase tracking-wider text-[var(--text-secondary)]">
                                {complaint.type === "Request" ? "Target Type" : "Mentioned Employee"}
                              </p>
                              <p className="truncate text-xs font-black text-[var(--text-primary)]" title={complaint.type === "Request" ? "Admin Panel" : employeeLine(complaint.againstEmployee)}>
                                {complaint.type === "Request" ? "SaaS Operations Request" : (complaint.againstEmployee?.name || "Unknown")}
                              </p>
                              <p className="truncate text-[9px] text-[var(--text-secondary)] mt-0.5">
                                {complaint.type === "Request" ? "System Workflow" : `${complaint.againstEmployee?.team || "No Team"} • ID: ${complaint.againstEmployee?.employeeId || "-"}`}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Ticket Description */}
                        <div className="relative bg-[var(--bg-secondary)] border border-[var(--border-color)]/60 rounded-2xl p-4">
                          <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--text-secondary)] font-semibold">
                            {complaint.description}
                          </p>
                        </div>

                        {/* Admin Resolution Note */}
                        {complaint.adminNote && (
                          <div className="mt-4 rounded-2xl border-l-4 border-emerald-500 bg-emerald-500/5 dark:bg-emerald-950/10 p-4 flex gap-3">
                            <Info size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                            <div className="text-xs">
                              <span className="font-black text-emerald-700 dark:text-emerald-400 block mb-1">
                                Resolution Remarks
                              </span>
                              <p className="text-[var(--text-secondary)] font-semibold leading-relaxed">
                                {complaint.adminNote}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Footer Controls */}
                        <div className="mt-5 border-t border-[var(--border-color)] pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="text-[10px] font-bold text-[var(--text-secondary)]">
                            Reference ID: {complaint._id}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {complaint.status !== "Reviewing" && (
                              <button
                                type="button"
                                onClick={() => handleUpdateStatusClick(complaint, "Reviewing")}
                                disabled={updatingId === complaint._id}
                                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--accent-primary)]/20 bg-[var(--accent-primary)]/5 hover:bg-[var(--accent-primary)]/10 px-4 text-xs font-black text-[var(--accent-primary)] dark:text-[var(--accent-primary)] transition-colors disabled:opacity-50"
                              >
                                <Activity size={13} />
                                Review
                              </button>
                            )}
                            {complaint.status !== "Resolved" && (
                              <button
                                type="button"
                                onClick={() => handleUpdateStatusClick(complaint, "Resolved")}
                                disabled={updatingId === complaint._id}
                                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10 px-4 text-xs font-black text-emerald-600 dark:text-emerald-400 transition-colors disabled:opacity-50"
                              >
                                <CheckCircle2 size={13} />
                                Resolve
                              </button>
                            )}
                            {complaint.status !== "Dismissed" && (
                              <button
                                type="button"
                                onClick={() => handleUpdateStatusClick(complaint, "Dismissed")}
                                disabled={updatingId === complaint._id}
                                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-slate-500/20 bg-[var(--bg-secondary)]0/5 hover:bg-[var(--bg-secondary)]0/10 px-4 text-xs font-black text-slate-600 dark:text-slate-400 transition-colors disabled:opacity-50"
                              >
                                <XCircle size={13} />
                                Dismiss
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(complaint)}
                              disabled={updatingId === complaint._id}
                              className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-red-500/20 bg-red-500/5 hover:bg-red-500 hover:text-white px-4 text-xs font-black text-red-600 hover:border-red-600 hover:shadow-sm disabled:opacity-50 transition-all"
                            >
                              <Trash2 size={13} />
                              Delete
                            </button>
                          </div>
                        </div>

                      </motion.article>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </section>

        </div>
      </main>

      {/* MODAL: Input Admin Note Modal */}
      <AnimatePresence>
        {noteModalOpen && noteModalComplaint && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setNoteModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.35 }}
              className="relative w-full max-w-lg rounded-3xl bg-[var(--card-bg)] border border-[var(--border-color)] overflow-hidden shadow-2xl flex flex-col z-10 p-6"
            >
              <h3 className="text-lg font-black tracking-tight text-[var(--text-primary)] flex items-center gap-2">
                <FileText className="text-[var(--accent-primary)]" size={20} />
                Add Admin Resolution Note
              </h3>
              <p className="mt-1.5 text-xs text-[var(--text-secondary)] font-semibold">
                Provide remarks for marking this {displayType(noteModalComplaint.type).toLowerCase()} as <span className="font-bold text-[var(--text-primary)]">{noteModalTargetStatus.toLowerCase()}</span>.
              </p>
              
              <div className="mt-4">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Enter resolution notes, explanations, or steps taken..."
                  className="w-full h-32 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 text-xs font-semibold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none"
                />
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setNoteModalOpen(false)}
                  className="h-10 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 px-5 text-xs font-bold text-[var(--text-primary)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    executeStatusUpdate(noteModalComplaint, noteModalTargetStatus, noteText);
                    setNoteModalOpen(false);
                  }}
                  className="h-10 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] shadow-sm px-5 text-xs font-bold text-white shadow-md shadow-[var(--accent-primary)]/15 transition-colors"
                >
                  Save & Update Status
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: Confirmation Delete Modal */}
      <AnimatePresence>
        {deleteModalOpen && deleteModalComplaint && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDeleteModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.35 }}
              className="relative w-full max-w-md rounded-3xl bg-[var(--card-bg)] border border-[var(--border-color)] overflow-hidden shadow-2xl flex flex-col z-10 p-6"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500/10 text-red-600 mb-4">
                <AlertCircle size={24} />
              </div>
              <h3 className="text-lg font-black tracking-tight text-[var(--text-primary)]">
                Delete Permanent Confirmation
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-[var(--text-secondary)] font-semibold">
                Are you absolutely sure you want to delete this {displayType(deleteModalComplaint.type).toLowerCase()} permanently? This action is irreversible and will remove all record details.
              </p>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteModalOpen(false)}
                  className="h-10 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 px-5 text-xs font-bold text-[var(--text-primary)] transition-colors"
                >
                  No, Keep It
                </button>
                <button
                  type="button"
                  onClick={() => {
                    executeDelete(deleteModalComplaint);
                    setDeleteModalOpen(false);
                  }}
                  className="h-10 rounded-xl bg-red-600 hover:bg-red-700 px-5 text-xs font-bold text-white shadow-md shadow-red-500/10 transition-colors"
                >
                  Yes, Delete Permanently
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminComplaints;


